// timelineUtils.js
// Utility for generating timeline data for assessment modules

/**
 * Generates timeline items for a given pengajuan (submission) object.
 * @param {Object} pengajuan - The submission object.
 * @param {Object} employeeData - The employee data object (must have namaPegawai, nip).
 * @param {Function} formatDateSafe - Function(date, formatString) to format dates safely.
 * @returns {Array} timelineItems - Array of timeline item objects.
 */
export function generateTimelineDataUtil(pengajuan, employeeData, formatDateSafe) {
  const timelineItems = [];
  const pushTimeline = (date, status, title, description, timeFmt = 'HH:mm', dateFmt = 'DD MMM YYYY') => {
    const d = new Date(date);
    timelineItems.push({
      date: formatDateSafe(d, dateFmt),
      time: formatDateSafe(d, timeFmt) + ' WIB',
      status,
      title,
      description,
    });
  };

  // Initial submission
  if (pengajuan.tanggal_pengajuan) {
    pushTimeline(
      pengajuan.tanggal_pengajuan,
      'default',
      'Inisiasi Pengajuan Pembinaan PeG',
      `Pegawai ${employeeData.namaPegawai} dengan NIP ${employeeData.nip} telah menginisiasi Pengajuan Pembinaan PeG`,
    );
  }

  // Approval process
  if (['menunggu_approval_atasan', 'pending'].includes(pengajuan.status)) {
    pushTimeline(new Date(), 'processing', 'Proses Persetujuan', 'Pengajuan sedang dalam proses persetujuan atasan');
  }

  // Approval steps config
  const approvalSteps = [
    {
      statusKey: 'approval_atasan_status',
      atKey: 'approval_atasan_at',
      approverKey: 'approver_atasan',
      label: 'Approval Atasan',
      status: 'processing',
      getDescription: (pengajuan, dateFmt) =>
        `Approval Atasan: Disetujui oleh ${pengajuan.approver_atasan?.name || '-'} pada ${formatDateSafe(
          new Date(pengajuan.approval_atasan_at),
          dateFmt,
        )} WIB<br>`,
    },
    {
      statusKey: 'approval_bod1_status',
      atKey: 'approval_bod1_at',
      approverKey: 'approver_bod1',
      label: 'Approval BOD',
      status: 'processing',
      getDescription: (pengajuan, dateFmt) =>
        `Approval BOD-1: Disetujui oleh ${pengajuan.approver_bod1?.name || '-'} pada ${formatDateSafe(
          new Date(pengajuan.approval_bod1_at),
          dateFmt,
        )} WIB<br>`,
    },
    {
      statusKey: 'status',
      atKey: 'approval_hc_at',
      approverKey: null,
      label: 'Approval HC',
      status: 'success',
      requiredStatus: 'disetujui_hc',
      getDescription: (pengajuan, dateFmt) => {
        let desc = `Approval HC: Disetujui oleh HC pada ${formatDateSafe(
          new Date(pengajuan.approval_hc_at),
          dateFmt,
        )} WIB<br>`;
        if (pengajuan.hc_notes != null) desc += `Catatan: ${pengajuan.hc_notes}`;
        return desc;
      },
      dateFmt: 'DD MMMM YYYY HH:mm',
    },
  ];

  approvalSteps.forEach(step => {
    const requiredStatus = step.requiredStatus || 'approved';
    if (
      (step.statusKey === 'status'
        ? pengajuan[step.statusKey] === requiredStatus
        : pengajuan[step.statusKey] === 'approved') &&
      pengajuan[step.atKey]
    ) {
      pushTimeline(
        pengajuan[step.atKey],
        step.status,
        'Detail Persetujuan',
        step.getDescription(pengajuan, step.dateFmt || 'DD MMM YYYY HH:mm'),
        'HH:mm',
        'DD MMM YYYY',
      );
    }
  });

  // Final approval
  if (pengajuan.status === 'disetujui') {
    pushTimeline(
      new Date(),
      'success',
      'Selesai Disetujui',
      'Pengajuan Pembinaan PeG telah disetujui',
      'HH:mm',
      'DD MM YYYY',
    );
  }

  // Rejection
  if (pengajuan.status === 'ditolak') {
    pushTimeline(
      new Date(),
      'error',
      'Pengajuan Ditinjau Ulang',
      pengajuan.alasan_tinjau_ulang ? 'Keterangan: ' + pengajuan.alasan_tinjau_ulang : '',
      'HH:mm',
      'DD MMM YYYY',
    );
  }

  // Rejection Hc
  if (pengajuan.status === 'ditolak_hc') {
    pushTimeline(
      new Date(),
      'error',
      'Pengajuan Ditinjau Ulang Oleh HC',
      pengajuan.alasan_tinjau_ulang ? 'Keterangan: ' + pengajuan.alasan_tinjau_ulang : '',
      'HH:mm',
      'DD MMM YYYY',
    );
  }

  return timelineItems;
}
